import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button.js';
import { CheckCircle2, Loader2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import type { TfaPanelFull, TfaDeliberationResponse } from '@edison/api-client-react';
import { usePostTfaPanelistDeliberate } from '@edison/api-client-react';

const QUESTIONS = [
  {
    key: 'systematic_investigation' as const,
    label: 'Experimental Approach',
    question: 'Was the approach experimental — forming and testing hypotheses rather than applying known solutions?',
    guidance: 'Division 355 requires R&D to be conducted in a systematic, investigative and experimental manner. This means the team formed hypotheses about what might work, designed tests or structured experiments to evaluate them, and drew conclusions from the results — as opposed to skilled but routine development work.',
  },
  {
    key: 'technical_uncertainty' as const,
    label: 'Technical Uncertainty',
    question: 'Was the outcome genuinely unknown before the work began — even to an experienced practitioner?',
    guidance: 'AusIndustry requires that no competent professional in the field, with full access to existing knowledge, could have predicted the outcome without running experiments. If a skilled practitioner could have written the solution upfront knowing it would work, this criterion is not met.',
  },
  {
    key: 'new_knowledge' as const,
    label: 'New Knowledge',
    question: 'Was the goal to discover something new, rather than apply what was already known?',
    guidance: 'Core R&D activities must aim to generate new knowledge — about how to create new or improved materials, products, processes, or services. If the team was applying established techniques (even in a complex or novel combination), this criterion may not be satisfied.',
  },
  {
    key: 'innovative' as const,
    label: 'Advance in Capability',
    question: 'Does this work push beyond what was previously possible, or is it skilled use of existing tools?',
    guidance: 'AusIndustry looks for evidence that the project advanced the state of knowledge or capability — not just that it was technically difficult. Applying a well-documented algorithm to a new dataset is not R&D even if it is hard. The question is whether the work expanded what was known to be achievable.',
  },
  {
    key: 'project_specific_uncertainty' as const,
    label: 'Project-Specific Uncertainty',
    question: 'Was the uncertainty about whether this specific technical approach would work — not just that nobody had done it before?',
    guidance: '"No one has done this globally" can describe routine development. The real question is whether this team faced a specific technical challenge they couldn\'t resolve without experimentation. The uncertainty must relate to a particular problem this project had, not general industry unfamiliarity with a technology.',
  },
] as const;

type QuestionKey = typeof QUESTIONS[number]['key'];

interface DeliberationFormValues {
  answers: Record<QuestionKey, 'yes' | 'no' | ''>;
  overallComment: string;
}

interface Props {
  tfa: TfaPanelFull & { myDeliberation?: TfaDeliberationResponse | null };
  myPanelistId: string;
  onSubmitted: () => void;
}

export function TfaDeliberationWorkbench({ tfa, myPanelistId, onSubmitted }: Props) {
  const submitDeliberation = usePostTfaPanelistDeliberate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [contextExpanded, setContextExpanded] = useState(false);

  const alreadySubmitted = !!tfa.myDeliberation?.submittedAt;

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<DeliberationFormValues>({
    defaultValues: {
      answers: alreadySubmitted
        ? Object.fromEntries(
            (tfa.myDeliberation!.responses as Array<{ questionKey: QuestionKey; answer: boolean }>)
              .map((r) => [r.questionKey, r.answer ? 'yes' : 'no'])
          ) as Record<QuestionKey, 'yes' | 'no' | ''>
        : Object.fromEntries(QUESTIONS.map((q) => [q.key, ''])) as Record<QuestionKey, 'yes' | 'no' | ''>,
      overallComment: tfa.myDeliberation?.overallComment ?? '',
    },
  });

  const answers = watch('answers');
  const allAnswered = QUESTIONS.every((q) => answers[q.key] === 'yes' || answers[q.key] === 'no');

  const onSubmit = async (data: DeliberationFormValues) => {
    setSubmitError(null);
    const responses = QUESTIONS.map((q) => ({
      questionKey: q.key,
      answer: data.answers[q.key] === 'yes',
      comment: null,
    }));
    try {
      await submitDeliberation.mutateAsync({
        panelId: tfa.id,
        panelistId: myPanelistId,
        data: { responses, overallComment: data.overallComment.trim() || null },
      });
      onSubmitted();
    } catch {
      setSubmitError('Failed to submit. Please try again.');
    }
  };

  return (
    <div className="space-y-5">
      {/* Reference context — collapsible */}
      <div className="rounded-lg border">
        <button
          type="button"
          onClick={() => setContextExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/40 transition-colors rounded-lg"
        >
          <span>Project Context (Problem Definition &amp; COTS Sweep)</span>
          {contextExpanded
            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
            : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </button>
        {contextExpanded && (
          <div className="px-4 pb-4 space-y-4 border-t pt-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Problem Definition</p>
              <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground whitespace-pre-wrap max-h-56 overflow-y-auto">
                {tfa.problemStatement || <span className="italic">Not yet defined</span>}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">COTS Sweep</p>
              <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground whitespace-pre-wrap max-h-56 overflow-y-auto">
                {tfa.cotsSweepNotes || <span className="italic">Not yet completed</span>}
              </div>
            </div>
          </div>
        )}
      </div>

      {alreadySubmitted && tfa.myDeliberation && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 flex items-center gap-3">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
          <p className="text-xs text-emerald-700">
            {(() => {
              const submitted = new Date(tfa.myDeliberation!.submittedAt!).toLocaleString('en-AU');
              const isEdited = tfa.myDeliberation!.updatedAt && tfa.myDeliberation!.updatedAt !== tfa.myDeliberation!.submittedAt;
              if (isEdited) {
                const edited = new Date(tfa.myDeliberation!.updatedAt!).toLocaleString('en-AU');
                return `Submitted ${submitted} · Edited ${edited}`;
              }
              return `Submitted ${submitted}`;
            })()}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
        <div className="rounded-lg border divide-y">
          {QUESTIONS.map((q) => (
            <div key={q.key} className="flex items-center justify-between gap-4 px-4 py-2.5">
              <p className="text-sm flex-1 min-w-0">{q.question}</p>
              <div className="flex gap-2 shrink-0">
                {(['yes', 'no'] as const).map((val) => (
                  <button
                    key={val}
                    type="button"
                    title={q.guidance}
                    onClick={() => setValue(`answers.${q.key}`, val, { shouldValidate: true })}
                    className={`rounded-md border px-3 py-1 text-sm transition-colors ${
                      answers[q.key] === val
                        ? val === 'yes'
                          ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
                          : 'border-destructive/50 bg-destructive/10 text-destructive'
                        : 'hover:bg-accent'
                    }`}
                  >
                    {val === 'yes' ? '✓' : '✗'}
                  </button>
                ))}
                <input type="hidden" {...register(`answers.${q.key}`, { required: true })} />
              </div>
              {errors.answers?.[q.key] && (
                <p className="text-xs text-destructive shrink-0">Required</p>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2 pt-3">
          <label htmlFor="overallComment" className="text-sm font-medium block">Comments</label>
          <textarea
            id="overallComment"
            {...register('overallComment')}
            placeholder="Your overall view on whether this project meets the R&D criteria…"
            rows={4}
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-none"
          />
        </div>

        {submitError && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {submitError}
          </div>
        )}

        <Button
          type="submit"
          disabled={!allAnswered || submitDeliberation.isPending}
          className="w-full"
        >
          {submitDeliberation.isPending ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {alreadySubmitted ? 'Updating…' : 'Submitting…'}
            </span>
          ) : (
            alreadySubmitted ? 'Update my deliberation' : 'Submit my deliberation'
          )}
        </Button>
        {!allAnswered && (
          <p className="text-xs text-muted-foreground text-center">Answer all 5 questions to submit</p>
        )}
      </form>
    </div>
  );
}
